import React, { Fragment, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from 'reactstrap';

export const Questionary = ({ questionary, setCorrect, questionaryAnswers, finishButton, cancelButton, feedbackComment, setFeedbackComment }) => {

    const [page, setPage] = useState(0);
    const totalPages = questionary.questions.length + 1; // +1 para la página de feedback
    const isFeedbackPage = page === questionary.questions.length;
    
    // Estado del temporizador
    const [timeLeft, setTimeLeft] = useState(null);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const hasTimer = questionary.timer && questionary.timer !== '' && questionary.timer !== null && questionary.timer > 0;

    const { t } = useTranslation();

    const handleNextQuestion = () => {
        if (page < totalPages - 1) {
            setPage(page + 1);
        }
    };

    const handlePrevQuestion = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };

    // Efecto para manejar el temporizador cuando cambia la página
    useEffect(() => {
        // Limpiar el temporizador anterior
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        // Solo activar el temporizador si hay timer configurado y no es la página de feedback
        if (hasTimer && !isFeedbackPage) {
            const timerSeconds = questionary.timer;
            startTimeRef.current = Date.now();
            setTimeLeft(timerSeconds);
            
            timerRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
                const remaining = timerSeconds - elapsed;
                
                if (remaining <= 0) {
                    // Tiempo agotado, pasar a la siguiente pregunta sin guardar respuesta
                    clearInterval(timerRef.current);
                    setTimeLeft(0);
                    handleNextQuestion();
                } else {
                    setTimeLeft(remaining);
                }
            }, 100); // Actualizar cada 100ms para mayor precisión
        } else {
            setTimeLeft(null);
            startTimeRef.current = null;
        }

        // Limpiar el intervalo al desmontar o cambiar de página
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, hasTimer, isFeedbackPage, questionary.timer]);

    return (
        <div>
            <div className='mb-5'>
                <h4>{questionary.name} <small>({`v${questionary.version}.0`})</small></h4>
            </div>
            <div style={{ minHeight: '300px' }}>
                <div className="card">
                    <div className="card-header">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className='d-flex flex-column'>
                                {isFeedbackPage ? (
                                    <>
                                        <h5>{t('questionary.feedback_title')}</h5>
                                        <i>{t('questionary.feedback_subtitle')}</i>
                                    </>
                                ) : (
                                    <>
                                        <h5>{questionary.questions[page].question}</h5>
                                        <i>{t('questionary.choose_answer')}</i>
                                    </>
                                )}
                            </div>
                            <div style={{ minWidth: '70px' }}>
                                <p className='bg-primary text-light fw-bold pb-3 ps-3 pt-3 pe-3 rounded-3'
                                >
                                    {page + 1} / {totalPages}
                                </p>
                            </div>
                        </div>
                    </div>
                    {isFeedbackPage ? (
                        <div className="card-body">
                            <Input
                                type='textarea'
                                id='feedback'
                                name='feedback'
                                rows={6}
                                placeholder={t('questionary.feedback_placeholder')}
                                value={feedbackComment || ''}
                                onChange={(e) => setFeedbackComment(e.target.value)}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    ) : (
                        <ul className="list-group list-group-flush">
                            {
                                questionary.questions[page].answers.map((answer, i) => (
                                    <Fragment key={i}>
                                        <li
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setCorrect(questionary.questions[page]._id, answer._id)}
                                            className={questionaryAnswers.some((questionaryAnswer) => {
                                                const answers = Array.isArray(questionaryAnswer.answer) ? questionaryAnswer.answer : (questionaryAnswer.answer ? [questionaryAnswer.answer] : []);
                                                return questionaryAnswer.question === questionary.questions[page]._id && answers.includes(answer._id);
                                            }) ? 'list-group-item d-flex gap-3 bg-success text-light' : 'list-group-item d-flex gap-3'}
                                        >
                                            <span>
                                                {answer.answer}
                                            </span>
                                        </li>
                                    </Fragment>
                                ))
                            }
                        </ul>
                    )}
                </div>
            </div>
            <div className="d-flex justify-content-between mt-3">
                <div className='d-flex gap-3 align-items-center'>
                    {cancelButton}
                    {
                        page === 0 ?
                            null
                            :
                            <Button 
                                color='primary' 
                                onClick={handlePrevQuestion}
                                disabled={hasTimer}
                            >
                                {t('questionary.prev_question')}
                            </Button>
                    }
                    {
                        isFeedbackPage ?
                            null
                            :
                            <Button color='primary' onClick={handleNextQuestion}>{t('questionary.next_question')}</Button>
                    }
                </div>
                <div className='d-flex gap-3 align-items-center'>
                    {hasTimer && !isFeedbackPage && timeLeft !== null && (
                        <div className={`px-3 py-2 rounded-3 fw-bold ${
                            timeLeft <= 10 ? 'bg-danger text-light' : 
                            timeLeft <= 30 ? 'bg-warning text-dark' : 
                            'bg-info text-light'
                        }`}>
                            {t('questionary.timer_remaining')}: {timeLeft}s
                        </div>
                    )}
                    {isFeedbackPage && finishButton}
                </div>
            </div>
        </div>
    )
}
